import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  FaUserMd,
  FaUserNurse,
  FaPills,
  FaFlask,
  FaXRay,
  FaNotesMedical,
  FaExclamationTriangle,
  FaFilter,
  FaChevronDown,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { MdLocalHospital, MdOutlineAssignment } from "react-icons/md";
import TimeLineService from "../../../../Services/TimeLineService";

const EVENT_CONFIG = {
  ADMISSION: {
    icon: <MdLocalHospital />,
    iconBg: "bg-[#7c3aed]",
    badgeStyle: "bg-purple-100 text-purple-700",
    badgeLabel: "Admission",
  },
  CLINICAL_NOTE: {
    icon: <FaNotesMedical />,
    iconBg: "bg-[#2563eb]",
    badgeStyle: "bg-blue-100 text-blue-700",
    badgeLabel: "Clinical Note",
  },
  LAB_ORDER: {
    icon: <FaFlask />,
    iconBg: "bg-[#059669]",
    badgeStyle: "bg-emerald-100 text-emerald-700",
    badgeLabel: "Lab Order",
  },
  MEDICATION: {
    icon: <FaPills />,
    iconBg: "bg-[#d97706]",
    badgeStyle: "bg-amber-100 text-amber-700",
    badgeLabel: "Medication",
  },
  RADIOLOGY: {
    icon: <FaXRay />,
    iconBg: "bg-[#0891b2]",
    badgeStyle: "bg-cyan-100 text-cyan-700",
    badgeLabel: "Radiology",
  },
  NURSING: {
    icon: <FaUserNurse />,
    iconBg: "bg-[#7c3aed]",
    badgeStyle: "bg-violet-100 text-violet-700",
    badgeLabel: "Nursing Note",
  },
  LAB_REPORT: {
    icon: <FaFlask />,
    iconBg: "bg-[#16a34a]",
    badgeStyle: "bg-green-100 text-green-700",
    badgeLabel: "Lab Report",
  },
  ORDER_STATUS: {
    icon: <MdOutlineAssignment />,
    iconBg: "bg-[#0d9488]",
    badgeStyle: "bg-teal-100 text-teal-700",
    badgeLabel: "Order Status",
  },
  DEFAULT: {
    icon: <FaNotesMedical />,
    iconBg: "bg-gray-400",
    badgeStyle: "bg-gray-100 text-gray-600",
    badgeLabel: "Event",
  },
};

const getEventConfig = (eventLabel) =>
  EVENT_CONFIG[eventLabel?.toUpperCase().replace(/ /g, "_")] ||
  EVENT_CONFIG.DEFAULT;

const formatDateTime = (value) => {
  if (!value) return { date: "—", time: "" };
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return { date: value, time: "" };
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { date, time };
};

const MOCK_EVENTS = [
  {
    id: 1,
    eventTimestamp: "2026-07-01 10:37 AM",
    title: "Patient Admitted",
    description:
      "Patient admitted in ICU - A, Bed - 12\nChief Complaint: Breathlessness and mild chest discomfort.",
    eventLabel: "ADMISSION",
    actorName: "Dr. Hari Prakash",
  },
  {
    id: 2,
    eventTimestamp: "2026-07-01 10:45 AM",
    title: "Clinical Note Added",
    description:
      "Patient is conscious and oriented. Complains of mild chest discomfort.\nBP and vitals stable. Advised to continue current medication.",
    eventLabel: "CLINICAL_NOTE",
    actorName: "Dr. Hari Prakash",
  },
  {
    id: 3,
    eventTimestamp: "2026-07-01 11:30 AM",
    title: "Lab Test Ordered",
    description: "CBC, RBS, Serum Electrolytes\nOrdered Tests: 3",
    eventLabel: "LAB_ORDER",
    actorName: "Dr. Hari Prakash",
  },
  {
    id: 4,
    eventTimestamp: "2026-07-01 12:15 PM",
    title: "Medication Ordered",
    description: "Paracetamol 650mg, Pantoprazole 40mg\nTotal Medicines: 2",
    eventLabel: "MEDICATION",
    actorName: "Dr. Hari Prakash",
  },
  {
    id: 5,
    eventTimestamp: "2026-07-01 02:00 PM",
    title: "Radiology Ordered",
    description: "Chest X-Ray (PA View)\nStatus: Completed",
    eventLabel: "RADIOLOGY",
    actorName: "Dr. Hari Prakash",
  },
  {
    id: 6,
    eventTimestamp: "2026-07-01 04:20 PM",
    title: "Nursing Note Added",
    description:
      "Patient vitals recorded. BP: 120/80 mmHg, Pulse: 82 bpm, SpO2: 98% on room air.\nPatient comfortable.",
    eventLabel: "NURSING",
    actorName: "Nurse Kavitha R",
  },
  {
    id: 7,
    eventTimestamp: "2026-07-01 06:10 PM",
    title: "Lab Report Available",
    description: "CBC Report\nStatus: Completed",
    eventLabel: "LAB_REPORT",
    actorName: "System",
  },
  {
    id: 8,
    eventTimestamp: "2026-07-01 08:30 PM",
    title: "Order Completed",
    description: "Chest X-Ray (PA View)\nStatus: Completed",
    eventLabel: "ORDER_STATUS",
    actorName: "System",
  },
];

function SkeletonRow() {
  return (
    <div className="flex items-start gap-6 animate-pulse py-4 border-b border-[#edf1f7]">
      <div className="flex-shrink-0 w-28 h-4 bg-gray-200 rounded mt-1" />
      <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-1/3 bg-gray-200 rounded" />
        <div className="h-3 w-2/3 bg-gray-200 rounded" />
        <div className="h-3 w-1/2 bg-gray-200 rounded" />
      </div>
      <div className="w-24 h-6 bg-gray-200 rounded-md flex-shrink-0" />
    </div>
  );
}

function Timeline({ patientDetails, patientId, admissionId }) {
  const [events, setEvents] = useState(MOCK_EVENTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("2026-06-01");
  const [toDate, setToDate] = useState("2026-07-01");

  const fetchEvents = useCallback(async () => {
    const payload = {
      pageNo: 1,
      pageSize: 10,
      patientId: patientDetails?.patientId,
    };
    try {
      const response = await TimeLineService.getTimeLineDetails(payload);
      console.log("fetchEvents", response);
      setEvents(response?.data || []);
    } catch (error) {
      console.error("Error fetching timeline events:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [patientId, fromDate, toDate]);
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) =>
        new Date(a.eventTimestamp.replace(" ", "T")) -
        new Date(b.eventTimestamp.replace(" ", "T")),
    );
  }, [events]);

  return (
    <div className="flex flex-col gap-5">
      {/* HEADER */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">Timeline</h2>
          <p className="text-[13px] text-[#6b7280] mt-1">
            Complete chronological view of patient activities, events and
            updates.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Pill */}
          <div className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg px-4 h-10 bg-white min-w-[240px] justify-between text-[#374151]">
            <div className="flex items-center gap-2 text-[13px] font-semibold">
              <FaRegCalendarAlt className="text-[#6b7280]" />
              <span>
                {fromDate
                  ? new Date(fromDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "From"}
                {" - "}
                {toDate
                  ? new Date(toDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "To"}
              </span>
            </div>
            <FaChevronDown className="text-[#6b7280] text-[10px]" />
          </div>

          {/* Filter Button */}
          <button className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg px-4 h-10 bg-white text-[13px] font-semibold text-[#4b5563] hover:bg-gray-50">
            <FaFilter className="text-[12px]" />
            Filter
          </button>
        </div>
      </div>

      {/* TIMELINE LIST */}
      <div className="bg-white border border-[#e9edf5] rounded-[18px] overflow-hidden">
        {loading && (
          <div className="px-6 py-4 divide-y divide-[#edf1f7]">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}

        {error && (
          <div className="m-6 flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[#b91c1c]">
            <FaExclamationTriangle />
            <p className="text-[13px] font-medium">{error}</p>
          </div>
        )}

        {!loading && !error && sortedEvents.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-[13px] text-[#9ca3af]">
              No timeline events found.
            </p>
          </div>
        )}

        {!loading && !error && sortedEvents.length > 0 && (
          <div>
            {sortedEvents.map((item, index) => {
              const cfg = getEventConfig(item.eventLabel);
              const { date, time } = formatDateTime(item.eventTimestamp);
              const isLast = index === sortedEvents.length - 1;

              return (
                <div
                  key={item.id}
                  className={`flex items-start gap-5 px-6 py-5 ${
                    !isLast ? "border-b border-[#edf1f7]" : ""
                  } hover:bg-[#fafbff] transition-colors`}
                >
                  {/* DATE-TIME (left column, fixed width) */}
                  <div className="w-36 flex-shrink-0 pt-1">
                    <p className="text-[12px] font-bold text-[#111827]">
                      {date}
                    </p>
                    <p className="text-[12px] text-[#6b7280] mt-0.5">{time}</p>
                  </div>

                  {/* ICON CIRCLE with connecting line */}
                  <div className="relative flex flex-col items-center flex-shrink-0">
                    <div
                      className={`w-10 h-10 rounded-full ${cfg.iconBg} flex items-center justify-center text-white text-[16px] z-10 shadow-sm`}
                    >
                      {cfg.icon}
                    </div>
                    {!isLast && (
                      <div className="w-[2px] bg-[#e5e7eb] flex-1 mt-1 min-h-[24px]" />
                    )}
                  </div>

                  {/* CONTENT (grows) */}
                  <div className="flex-1 min-w-0 pt-1">
                    <h3 className="text-[14px] font-bold text-[#111827]">
                      {item.title}
                    </h3>
                    <p className="text-[12px] text-[#4b5563] mt-1.5 leading-relaxed whitespace-pre-line">
                      {item.description}
                    </p>
                  </div>

                  {/* RIGHT SIDE: badge + actor */}
                  <div className="flex-shrink-0 flex flex-col items-end gap-1.5 pt-1 min-w-[130px]">
                    <span
                      className={`text-[10px] font-bold px-3 py-1 rounded-md ${cfg.badgeStyle}`}
                    >
                      {cfg.badgeLabel}
                    </span>
                    <p className="text-[11px] text-[#6b7280]">
                      {item.actorName}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Timeline;
